import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import { FaPencilAlt } from 'react-icons/fa';
import { useDispatch, useSelector } from 'react-redux';
import { setCurrentPage } from '../../../../../../Redux/slice/features/paginationSilce';
import { roomTariffColumns } from './RoomTariffData';
import DataTable from '../../../../../../Common/DataTable/DataTable';
import DataTableHeader from '../../../../../../Common/DataTableHeader/DataTableHeader';
import DataTableFooter from '../../../../../../Common/DataTableFooter/DataTableFooter';
import EditRoomTariffModal from './EditRoomTariff';
import { REACT_APP_API_URL } from '../../../../../../env';

const RoomTariffTable = () => {
  const dispatch = useDispatch();
  const { currentPage } = useSelector((state) => state.pagination);
  const itemsPerPage = 10;

  const [tariffs, setTariffs] = useState([]);
  const [hotels, setHotels] = useState([]);
  const [selectedHotel, setSelectedHotel] = useState(null);

  const [showEditModal, setShowEditModal] = useState(false);
  const [editData, setEditData] = useState(null);

  const hotelSelectStyles = useMemo(() => ({
    menuPortal: (base) => ({ ...base, zIndex: 9999 }),
    menu: (base) => ({ ...base, zIndex: 9999 })
  }), []);
  const selectPortalTarget = typeof document !== 'undefined' ? document.body : null;

  useEffect(() => {
    dispatch(setCurrentPage(1));
    fetchHotels();
    fetchTariffs();
  }, [dispatch]);

  const fetchHotels = useCallback(async () => {
    try {
      const res = await axios.get(`${REACT_APP_API_URL}api/room-tariff/get-hotels`);
      const formatted = (res.data.data || []).map(hotel => ({
        value: hotel.PropertyID,
        label: hotel.UnitName
      }));
      setHotels(formatted);
    } catch (err) {
      console.error('Failed to fetch hotels:', err);
      setHotels([]);
    }
  }, []);

  const fetchTariffs = useCallback(async (hotelId = 0) => {
    try {
      const res = await axios.post(`${REACT_APP_API_URL}api/room-tariff/get-tariff-Config`, {
        P_CHRACTION: 'V',
        P_INTPROPERTYID: hotelId,
        P_PAGENO: 1,
        P_PAGESIZE: 100
      });

      const formatted = (res.data.data || []).map((item, index) => ({
        Sl: index + 1,
        RoomType: item.HotelRoomConfigType,
        NoOfRooms: item.NoOfRoomsAvailable,
        PricePerDay: item.PricePerDay,
        ExtraPersonRate: item.ExtraPersonRate,
        EffectiveFromDate: item.EffectiveDate,
        EffectiveTillDate: item.EffectiveTill,
        ID: item.HotelTariffConfigID,
        PropertyID: item.PropertyID,
        RoomConfigID: item.HotelRoomConfigID
      }));

      setTariffs(formatted);
    } catch (err) {
      console.error('Error fetching tariff data:', err);
      setTariffs([]);
    }
  }, []);

  const handleHotelChange = useCallback((selectedOption) => {
    dispatch(setCurrentPage(1));

    if (!selectedOption || !selectedOption.value) {
      setSelectedHotel(null);
      fetchTariffs(0);
    } else {
      setSelectedHotel(selectedOption);
      fetchTariffs(selectedOption.value);
    }
  }, [dispatch, fetchTariffs]);

  const handleEditClick = useCallback((rowData) => {
    setEditData(rowData);
    setShowEditModal(true);
  }, []);

  const columnsWithAction = useMemo(() => [
    ...roomTariffColumns.filter(col => col.accessor !== 'actions'),
    {
      Header: 'Action',
      accessor: 'actions',
      Cell: ({ row }) => (
        <Button size="sm" variant="outline-dark" onClick={() => handleEditClick(row.original)}>
          <FaPencilAlt />
        </Button>
      )
    }
  ], [handleEditClick]);

  const totalPages = Math.ceil(tariffs.length / itemsPerPage);

  const currentItems = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return tariffs.slice(start, start + itemsPerPage);
  }, [tariffs, currentPage, itemsPerPage]);

  const handleNextPage = useCallback(() => {
    if (currentPage < totalPages) {
      dispatch(setCurrentPage(currentPage + 1));
    }
  }, [dispatch, currentPage, totalPages]);

  const handlePreviousPage = useCallback(() => {
    if (currentPage > 1) {
      dispatch(setCurrentPage(currentPage - 1));
    }
  }, [dispatch, currentPage]);

  return (
    <div>
      <div className="mb-3 d-flex align-items-center">
        <label className="form-label fw-bold me-2">Hotel Name:</label>
        <Select
          options={hotels}
          value={selectedHotel}
          onChange={handleHotelChange}
          placeholder="-- Select --"
          isClearable
          className="ml-6 flex-grow-3"
          menuPortalTarget={selectPortalTarget}
          menuPosition="fixed"
          styles={hotelSelectStyles}
        />
      </div>

      <div className="card">
        <div className="card-body">
          <DataTableHeader columns={columnsWithAction} data={tariffs} />
          <DataTable columns={columnsWithAction} data={currentItems} enablePagination={false} />
          <DataTableFooter
            dataT={tariffs}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            handleNextPage={handleNextPage}
            handlePreviousPage={handlePreviousPage}
          />
        </div>
      </div>

      {showEditModal && (
        <EditRoomTariffModal
          show={showEditModal}
          onHide={() => setShowEditModal(false)}
          data={editData}
          onRefresh={() => fetchTariffs(selectedHotel?.value || 0)}
        />
      )}
    </div>
  );
};

export default React.memo(RoomTariffTable);
